import {
  collection,
  onSnapshot,
  query,
  where,
  type Timestamp,
} from 'firebase/firestore'
import { apiBaseUrl } from 'constants/apiBaseUrl'
import { getAuthToken } from 'core/api/axiosClient'
import { db } from 'config/firebase'
import { TONE_OPTIONS } from './assistantGuideOptions'

export const ASSISTANT_PROFILES_COLLECTION = 'assistantProfiles'
export const ASSISTANT_ROUTINES_COLLECTION = 'assistantRoutines'
export const ASSISTANT_ROUTINE_RUNS_COLLECTION = 'assistantRoutineRuns'

export type AssistantGuide = {
  departments: string
  watch: string
  tone: string
  include: string
  avoid: string
}

export const EMPTY_ASSISTANT_GUIDE: AssistantGuide = {
  departments: 'all',
  watch: 'all',
  tone: 'direct',
  include: '',
  avoid: '',
}

export type AssistantProfileSummary = {
  id: string
  name: string
  details: string
  guide: AssistantGuide
  ownerId: string
  memberIds: string[]
  chatId: string
  updatedAt: number | null
}

export type AssistantRoutineKind = 'email' | 'report'
export type AssistantReportTopic = 'sales' | 'inventory' | 'custom'
export type AssistantRoutineSchedule = 'once' | 'daily' | 'weekdays' | 'weekly' | 'custom'
export type AssistantWeekday = 'Sun' | 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat'
export type AssistantRoutinePeriod =
  | 'last_24_hours'
  | 'today'
  | 'previous_day'
  | 'this_week'
  | 'last_7_days'
  | 'previous_week'
  | 'this_month'
  | 'last_30_days'
  | 'previous_month'
  | 'this_quarter'
  | 'previous_quarter'
  | 'this_year'
  | 'previous_year'

const ROUTINE_PERIODS: AssistantRoutinePeriod[] = [
  'last_24_hours',
  'today',
  'previous_day',
  'this_week',
  'last_7_days',
  'previous_week',
  'this_month',
  'last_30_days',
  'previous_month',
  'this_quarter',
  'previous_quarter',
  'this_year',
  'previous_year',
]

export function asAssistantRoutinePeriod(value: unknown): AssistantRoutinePeriod {
  return ROUTINE_PERIODS.includes(value as AssistantRoutinePeriod)
    ? (value as AssistantRoutinePeriod)
    : 'last_24_hours'
}

export type AssistantRoutineClock = {
  schedule: AssistantRoutineSchedule
  times: string[]
  days: AssistantWeekday[]
  date: string | null
}

export type AssistantRoutineSummary = {
  id: string
  kind: AssistantRoutineKind
  reportTopic: AssistantReportTopic | null
  name: string
  prompt: string
  code: string
  period: AssistantRoutinePeriod
  schedule: AssistantRoutineSchedule
  times: string[]
  days: AssistantWeekday[]
  date: string | null
  recipientUserIds: string[]
  recipientEmails: string[]
  enabled: boolean
  lastStatus: string
  nextRunAt: number | null
  manualLockedUntil: number | null
}

export type AssistantRoutineRunStatus = 'running' | 'sent' | 'error'

export type AssistantRoutineRun = {
  id: string
  routineId: string
  assistantId: string
  label: string
  status: AssistantRoutineRunStatus
  startedAt: number | null
  error: string
}

export function assistantRunNowDisabled(input: {
  now: number
  manualLockedUntil: number | null
  running: boolean
}): boolean {
  if (input.running) return true
  return input.manualLockedUntil != null && input.manualLockedUntil > input.now
}

export type AssistantRoutineInput = {
  kind: AssistantRoutineKind
  reportTopic?: AssistantReportTopic | null
  name?: string
  prompt: string
  period?: AssistantRoutinePeriod
  schedule: AssistantRoutineSchedule
  times?: string[]
  days?: AssistantWeekday[]
  date?: string | null
  recipientUserIds?: string[]
  recipientEmails?: string[]
  enabled?: boolean
  enhance?: string
}

function readGuide(value: unknown): AssistantGuide {
  const record = value && typeof value === 'object' ? (value as Record<string, unknown>) : {}
  const text = (key: keyof AssistantGuide, fallback: string) =>
    typeof record[key] === 'string' ? record[key].trim() : fallback
  return {
    departments: text('departments', EMPTY_ASSISTANT_GUIDE.departments) || 'all',
    watch: text('watch', EMPTY_ASSISTANT_GUIDE.watch) || 'all',
    tone: TONE_OPTIONS.some(option => option.id === text('tone', EMPTY_ASSISTANT_GUIDE.tone))
      ? text('tone', EMPTY_ASSISTANT_GUIDE.tone)
      : EMPTY_ASSISTANT_GUIDE.tone,
    include: '',
    avoid: '',
  }
}

function millis(value: unknown): number | null {
  const stamp = value as Timestamp | undefined
  return typeof stamp?.toMillis === 'function' ? stamp.toMillis() : null
}

async function authHeaders(): Promise<HeadersInit> {
  const token = await getAuthToken()
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

async function readError(response: Response, fallback: string): Promise<string> {
  try {
    const body = (await response.json()) as { error?: string }
    if (body.error) return body.error
  } catch {
    // ignore
  }
  return fallback
}

export function subscribeAssistantProfiles(
  uid: string,
  onChange: (profiles: AssistantProfileSummary[]) => void,
  onError?: () => void,
): () => void {
  const owned = query(
    collection(db, ASSISTANT_PROFILES_COLLECTION),
    where('memberIds', 'array-contains', uid),
  )
  return onSnapshot(
    owned,
    snapshot => {
      const profiles = snapshot.docs.map(item => {
        const data = item.data()
        const memberIds = Array.isArray(data.memberIds)
          ? data.memberIds.filter((id): id is string => typeof id === 'string')
          : []
        return {
          id: item.id,
          name: typeof data.name === 'string' && data.name.trim() ? data.name.trim() : 'Assistant',
          details: typeof data.details === 'string' ? data.details : '',
          guide: readGuide(data.guide),
          ownerId: typeof data.ownerId === 'string' ? data.ownerId : '',
          memberIds,
          chatId: typeof data.chatId === 'string' ? data.chatId : '',
          updatedAt: millis(data.updatedAt),
        }
      })
      profiles.sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0) || a.name.localeCompare(b.name))
      onChange(profiles)
    },
    () => {
      if (onError) onError()
      else onChange([])
    },
  )
}

export function subscribeAssistantRoutines(
  assistantId: string,
  uid: string,
  onChange: (routines: AssistantRoutineSummary[]) => void,
  onError?: () => void,
): () => void {
  const owned = query(
    collection(db, ASSISTANT_ROUTINES_COLLECTION),
    where('memberIds', 'array-contains', uid),
  )
  return onSnapshot(
    owned,
    snapshot => {
      onChange(
        snapshot.docs.filter(item => item.data().assistantId === assistantId).map(item => {
          const data = item.data()
          const kind = data.kind === 'report' ? 'report' : 'email'
          const topic = data.reportTopic
          return {
            id: item.id,
            kind,
            reportTopic:
              topic === 'sales' || topic === 'inventory' || topic === 'custom' ? topic : null,
            name: typeof data.name === 'string' ? data.name : '',
            prompt: typeof data.prompt === 'string' ? data.prompt : '',
            code: typeof data.code === 'string' ? data.code : '',
            period: asAssistantRoutinePeriod(data.period),
            schedule:
              data.schedule === 'once' ||
              data.schedule === 'weekdays' ||
              data.schedule === 'weekly' ||
              data.schedule === 'custom'
                ? data.schedule
                : 'daily',
            times: (() => {
              const times = Array.isArray(data.times)
                ? data.times.filter((item): item is string => typeof item === 'string' && /^\d{2}:\d{2}$/.test(item))
                : []
              return times.length ? times : ['08:00']
            })(),
            days: Array.isArray(data.days)
              ? data.days.filter(
                  (item): item is AssistantWeekday =>
                    item === 'Sun' ||
                    item === 'Mon' ||
                    item === 'Tue' ||
                    item === 'Wed' ||
                    item === 'Thu' ||
                    item === 'Fri' ||
                    item === 'Sat',
                )
              : data.schedule === 'weekly'
                ? ['Mon']
                : [],
            date: typeof data.date === 'string' ? data.date : null,
            recipientUserIds: Array.isArray(data.recipientUserIds)
              ? data.recipientUserIds.filter((item): item is string => typeof item === 'string')
              : [],
            recipientEmails: Array.isArray(data.recipientEmails)
              ? data.recipientEmails.filter((item): item is string => typeof item === 'string')
              : [],
            enabled: data.enabled !== false,
            lastStatus: typeof data.lastStatus === 'string' ? data.lastStatus : '',
            nextRunAt: millis(data.nextRunAt),
            manualLockedUntil: millis(data.manualLockedUntil),
          }
        }),
      )
    },
    () => {
      if (onError) onError()
      else onChange([])
    },
  )
}

export function subscribeAssistantRoutineRuns(
  assistantId: string,
  uid: string,
  onChange: (runs: AssistantRoutineRun[]) => void,
  onError?: () => void,
): () => void {
  const owned = query(
    collection(db, ASSISTANT_ROUTINE_RUNS_COLLECTION),
    where('memberIds', 'array-contains', uid),
  )
  return onSnapshot(
    owned,
    snapshot => {
      const runs = snapshot.docs
        .filter(item => item.data().assistantId === assistantId)
        .map(item => {
          const data = item.data()
          const status = data.status === 'sent' || data.status === 'error' ? data.status : 'running'
          return {
            id: item.id,
            routineId: typeof data.routineId === 'string' ? data.routineId : '',
            assistantId,
            label: typeof data.label === 'string' && data.label.trim() ? data.label.trim() : 'Routine',
            status,
            startedAt: millis(data.startedAt),
            error: typeof data.error === 'string' ? data.error : '',
          } satisfies AssistantRoutineRun
        })
      runs.sort((a, b) => {
        if (a.status === 'running' && b.status !== 'running') return -1
        if (b.status === 'running' && a.status !== 'running') return 1
        return (b.startedAt ?? 0) - (a.startedAt ?? 0)
      })
      onChange(runs)
    },
    () => {
      if (onError) onError()
      else onChange([])
    },
  )
}

export async function createAssistantProfile(input: {
  name: string
  details: string
  guide: AssistantGuide
  memberId?: string
}): Promise<{ id: string; chatId: string }> {
  const response = await fetch(`${apiBaseUrl}/portal/admin/assistant/profiles`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(input),
  })
  if (!response.ok) throw new Error(await readError(response, 'Could not create that assistant'))
  return (await response.json()) as { id: string; chatId: string }
}

export async function updateAssistantProfile(
  id: string,
  input: { name: string; details: string; guide: AssistantGuide },
): Promise<void> {
  const response = await fetch(`${apiBaseUrl}/portal/admin/assistant/profiles/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: await authHeaders(),
    body: JSON.stringify(input),
  })
  if (!response.ok) throw new Error(await readError(response, 'Could not save that assistant'))
}

export async function inviteAssistantMember(id: string, memberId: string): Promise<void> {
  const response = await fetch(
    `${apiBaseUrl}/portal/admin/assistant/profiles/${encodeURIComponent(id)}/members`,
    {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({ memberId }),
    },
  )
  if (!response.ok) throw new Error(await readError(response, 'Could not add that admin'))
}

export async function removeAssistantMember(id: string, memberId: string): Promise<void> {
  const response = await fetch(
    `${apiBaseUrl}/portal/admin/assistant/profiles/${encodeURIComponent(id)}/members/${encodeURIComponent(memberId)}`,
    { method: 'DELETE', headers: await authHeaders() },
  )
  if (!response.ok) throw new Error(await readError(response, 'Could not remove that admin'))
}

export async function deleteAssistantProfile(id: string): Promise<void> {
  const response = await fetch(`${apiBaseUrl}/portal/admin/assistant/profiles/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  })
  if (!response.ok) throw new Error(await readError(response, 'Could not delete that assistant'))
}

export async function suggestRoutineName(prompt: string): Promise<string> {
  const response = await fetch(`${apiBaseUrl}/portal/admin/assistant/routines/name`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ prompt }),
  })
  if (!response.ok) throw new Error(await readError(response, 'Could not name that routine'))
  const body = (await response.json()) as { name?: string }
  return typeof body.name === 'string' ? body.name : ''
}

export async function createAssistantRoutine(assistantId: string, input: AssistantRoutineInput): Promise<void> {
  const response = await fetch(
    `${apiBaseUrl}/portal/admin/assistant/profiles/${encodeURIComponent(assistantId)}/routines`,
    {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify(input),
    },
  )
  if (!response.ok) throw new Error(await readError(response, 'Could not save that routine'))
}

export async function updateAssistantRoutine(id: string, input: AssistantRoutineInput): Promise<void> {
  const response = await fetch(`${apiBaseUrl}/portal/admin/assistant/routines/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: await authHeaders(),
    body: JSON.stringify(input),
  })
  if (!response.ok) throw new Error(await readError(response, 'Could not update that routine'))
}

export async function deleteAssistantRoutine(id: string): Promise<void> {
  const response = await fetch(`${apiBaseUrl}/portal/admin/assistant/routines/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  })
  if (!response.ok) throw new Error(await readError(response, 'Could not delete that routine'))
}

export async function runAssistantRoutineNow(id: string): Promise<void> {
  const response = await fetch(
    `${apiBaseUrl}/portal/admin/assistant/routines/${encodeURIComponent(id)}/run`,
    {
      method: 'POST',
      headers: await authHeaders(),
    },
  )
  if (!response.ok) throw new Error(await readError(response, 'Could not run that routine'))
}
